import React from "react";
import { useMutation } from "@apollo/client";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { Formik, Form as FoForm, Field, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import { COPY_CLASS, GET_CLASSES_QUERY } from "./queries";
import { get_list_query_variables } from "./tools";
import * as Yup from "yup";
import { dateToLocalISO, dateToLocalISOTime } from "../../../tools/date_tools";
import CSDatePicker from "../../ui/CSDatePicker";
import CSTimePicker from "../../ui/CSTimePicker";

import { Button, Form, Grid } from "tabler-react";
import { v4 } from "uuid";

// Define a Yup schema for the copy form
const COPY_CLASS_SCHEMA = Yup.object().shape({
  frequencyType: Yup.string().required("general.error_field_required"),
  frequencyInterval: Yup.number().required("general.error_field_required"),
  dateStart: Yup.date().required("general.error_field_required"),
  timeStart: Yup.date().required("general.error_field_required"),
  timeEnd: Yup.date().required("general.error_field_required"),
});

function ScheduleClassCopy({ t, scheduleItemId, date, isOpen, onClose }) {
  const [copyScheduleClass] = useMutation(COPY_CLASS);

  // If not open, don't render anything
  if (!isOpen) return null;

  // CSS for overlay
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  // CSS for modal
  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "5px",
    width: "450px",
    maxWidth: "90%",
    maxHeight: "90%",
    overflowY: "auto",
    zIndex: 1001,
    padding: "20px",
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3>{t("schedule.classes.copy")}</h3>

        <Formik
          initialValues={{
            frequencyType: "WEEKLY",
            frequencyInterval: 1,
            dateStart: new Date(),
            dateEnd: null,
            timeStart: new Date(),
            timeEnd: new Date(new Date().setHours(new Date().getHours() + 1)),
          }}
          validationSchema={COPY_CLASS_SCHEMA}
          onSubmit={(values, { setSubmitting }) => {
            console.log("submit values:");
            console.log(values);

            let frequencyInterval = values.frequencyInterval;
            if (values.frequencyType === "SPECIFIC") frequencyInterval = 0;

            let dateEnd;
            if (values.dateEnd) {
              dateEnd = dateToLocalISO(values.dateEnd);
            } else {
              dateEnd = values.dateEnd;
            }

            copyScheduleClass({
              variables: {
                input: {
                  id: scheduleItemId,
                  frequencyType: values.frequencyType,
                  frequencyInterval:
                    frequencyInterval && parseInt(frequencyInterval),
                  dateStart: dateToLocalISO(values.dateStart),
                  dateEnd: dateEnd,
                  timeStart: dateToLocalISOTime(values.timeStart),
                  timeEnd: dateToLocalISOTime(values.timeEnd),
                },
              },
              refetchQueries: [
                {
                  query: GET_CLASSES_QUERY,
                  variables: get_list_query_variables(),
                },
              ],
            })
              .then(({ data }) => {
                console.log("got data", data);
                toast.success(t("schedule.classes.toast_copy_success"), {
                  position: toast.POSITION.BOTTOM_RIGHT,
                });
                onClose();
              })
              .catch((error) => {
                toast.error(t("general.toast_server_error") + error, {
                  position: toast.POSITION.BOTTOM_RIGHT,
                });
                console.log("there was an error sending the query", error);
                setSubmitting(false);
              });
          }}
        >
          {({
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            values,
            errors,
            touched,
          }) => (
            <FoForm>
              <Form.Group label={t("schedule.classes.frequencyType")}>
                <Field
                  component="select"
                  name="frequencyType"
                  className={
                    errors.frequencyType
                      ? "form-control is-invalid"
                      : "form-control"
                  }
                  autoComplete="off"
                >
                  <option value="SPECIFIC" key={v4()}>
                    {t("schedule.classes.select_specific")}
                  </option>
                  <option value="WEEKLY" key={v4()}>
                    {t("schedule.classes.select_weekly")}
                  </option>
                  <option value="LAST_WEEKDAY_OF_MONTH" key={v4()}>
                    {t("schedule.classes.select_last_weekday_of_month")}
                  </option>
                </Field>
                <ErrorMessage
                  name="frequencyType"
                  component="span"
                  className="invalid-feedback"
                />
              </Form.Group>

              {values.frequencyType === "SPECIFIC" ? (
                ""
              ) : (
                <Form.Group label={t("schedule.classes.frequencyInterval")}>
                  <Field
                    component="select"
                    name="frequencyInterval"
                    className={
                      errors.frequencyInterval
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    autoComplete="off"
                  >
                    <option value="1" key={v4()}>
                      {t("general.monday")}
                    </option>
                    <option value="2" key={v4()}>
                      {t("general.tuesday")}
                    </option>
                    <option value="3" key={v4()}>
                      {t("general.wednesday")}
                    </option>
                    <option value="4" key={v4()}>
                      {t("general.thursday")}
                    </option>
                    <option value="5" key={v4()}>
                      {t("general.friday")}
                    </option>
                    <option value="6" key={v4()}>
                      {t("general.saturday")}
                    </option>
                    <option value="7" key={v4()}>
                      {t("general.sunday")}
                    </option>
                  </Field>
                  <ErrorMessage
                    name="frequencyInterval"
                    component="span"
                    className="invalid-feedback"
                  />
                </Form.Group>
              )}

              <Form.Group
                label={
                  values.frequencyType === "SPECIFIC"
                    ? t("general.date")
                    : t("general.date_start")
                }
              >
                <CSDatePicker
                  className={
                    errors.dateStart
                      ? "form-control is-invalid"
                      : "form-control"
                  }
                  selected={values.dateStart}
                  onChange={(date) => {
                    setFieldValue("dateStart", date);
                    setFieldTouched("dateStart", true);
                  }}
                  onBlur={() => setFieldTouched("dateStart", true)}
                />
                <ErrorMessage
                  name="dateStart"
                  component="span"
                  className="invalid-feedback"
                />
              </Form.Group>

              {values.frequencyType === "SPECIFIC" ? (
                ""
              ) : (
                <Form.Group label={t("general.date_end")}>
                  <CSDatePicker
                    selected={values.dateEnd}
                    onChange={(date) => {
                      setFieldValue("dateEnd", date);
                      setFieldTouched("dateEnd", true);
                    }}
                    onBlur={() => setFieldTouched("dateEnd", true)}
                    placeholderText={t("schedule.classes.placeholder_enddate")}
                  />
                  <ErrorMessage
                    name="dateEnd"
                    component="span"
                    className="invalid-feedback"
                  />
                </Form.Group>
              )}

              <Grid.Row>
                <Grid.Col>
                  <Form.Group label={t("general.time_start")}>
                    <CSTimePicker
                      className={
                        errors.timeStart
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      selected={values.timeStart}
                      onChange={(date) => setFieldValue("timeStart", date)}
                      onBlur={() => setFieldTouched("timeStart", true)}
                      clearable={false}
                    />
                    <ErrorMessage
                      name="timeStart"
                      component="span"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col>
                  <Form.Group label={t("general.time_end")}>
                    <CSTimePicker
                      className={
                        errors.timeEnd
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      selected={values.timeEnd}
                      onChange={(date) => setFieldValue("timeEnd", date)}
                      onBlur={() => setFieldTouched("timeEnd", true)}
                      clearable={false}
                    />
                    <ErrorMessage
                      name="timeEnd"
                      component="span"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Grid.Col>
              </Grid.Row>

              <div className="d-flex justify-content-end mt-4">
                <Button
                  color="secondary"
                  className="mr-2"
                  onClick={onClose}
                  type="button"
                >
                  {t("general.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {t("general.submit")}
                </Button>
              </div>
            </FoForm>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default withTranslation()(withRouter(ScheduleClassCopy));
